import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Link, LangRedirect } from "@/components/LangLink";
import { Check, ChevronLeft, AlertTriangle, ArrowRight, ArrowUpRight, Thermometer } from "lucide-react";
import { CTASection } from "@/components/sections";
import { Reveal, Stagger, StaggerItem } from "@/components/primitives";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ProductGallery, ColorSelector, OptionSelector, RequestInfoModal } from "@/components/product-detail";
import { getBendaggioProduct, familyLabel, relatedBendaggi } from "@/lib/bendaggi";
import { useAssistant } from "@/contexts/AssistantContext";
import { bendaggioContext } from "@/lib/productContext";

const MaterialTable = ({ rows }) => (
  <dl className="overflow-hidden rounded-xl border border-[#E7EDF2]">
    {rows.map((m, i) => (
      <div key={m[0]} className={`flex justify-between gap-4 px-4 py-2.5 ${i % 2 === 0 ? "bg-cloud" : "bg-white"}`}>
        <dt className="text-sm text-navy/60">{m[0]}</dt>
        <dd className="text-sm font-medium text-navy text-right">{m[1]}</dd>
      </div>
    ))}
  </dl>
);

// Scheda tecnica sintetica: solo righe con valore reale.
const TechTable = ({ rows }) => (
  <dl className="overflow-hidden rounded-xl border border-[#E7EDF2]">
    {rows.filter((r) => r[1]).map((r, i) => (
      <div key={r[0]} className={`flex justify-between gap-4 px-4 py-2.5 ${i % 2 === 0 ? "bg-cloud" : "bg-white"}`}>
        <dt className="text-sm text-navy/60">{r[0]}</dt>
        <dd className="text-sm font-medium text-navy text-right">{r[1]}</dd>
      </div>
    ))}
  </dl>
);

const RelatedCard = ({ p }) => (
  <StaggerItem className="h-full">
    <Link
      to={`/prodotti/bendaggi/${p.slug}`}
      data-testid={`bendaggio-related-${p.slug}`}
      className="group flex h-full flex-col rounded-2xl border border-[#E7EDF2] bg-white p-5 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-green hover:shadow-[0_20px_44px_-20px_rgba(15,25,35,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green focus-visible:ring-offset-2"
    >
      <span className="text-[10px] font-semibold uppercase tracking-wider text-green-deep">{p.tipologia}</span>
      <h3 className="mt-1 font-serif text-lg font-bold text-navy">{p.name}</h3>
      <p className="mt-1.5 flex-1 text-sm text-muted-foreground leading-relaxed">{p.short}</p>
      <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-green">
        Scopri il prodotto <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
      </span>
    </Link>
  </StaggerItem>
);

const BendaggioProduct = () => {
  const { slug } = useParams();
  const product = getBendaggioProduct(slug);

  const [color, setColor] = useState(null);
  const [larghezza, setLarghezza] = useState(null);
  const [lunghezza, setLunghezza] = useState(null);
  const [confezione, setConfezione] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const { setProductCtx } = useAssistant();
  useEffect(() => {
    if (product) setProductCtx(bendaggioContext(product));
    return () => setProductCtx(null);
  }, [product, setProductCtx]);

  // SEO: meta description + dati strutturati Product (senza prezzo/offerta).
  useEffect(() => {
    if (!product) return;
    const desc = product.short || product.description;
    const meta = document.head.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute("content") : null;
    if (meta) meta.setAttribute("content", desc);

    const ld = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.fullName || product.name,
      category: `Bendaggi / ${familyLabel(product.family)}`,
      description: product.description,
    };
    if (product.brand) ld.brand = { "@type": "Brand", name: product.brand };
    if (product.material?.length) ld.material = product.material.map((m) => `${m[0]} ${m[1]}`).join(", ");
    const imgs = (product.photos || []).map((p) => p.src);
    if (imgs.length) ld.image = imgs.map((s) => `${window.location.origin}${s}`);

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-dyn", "product-ld");
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);

    return () => {
      if (meta && prevDesc != null) meta.setAttribute("content", prevDesc);
      script.remove();
    };
  }, [product]);

  if (!product) return <LangRedirect to="/prodotti/bendaggi" />;

  const famLabel = familyLabel(product.family);
  const famLink = `/prodotti/bendaggi?fam=${product.family}`;
  const related = relatedBendaggi(product);

  const hasColorSelector = (product.colors?.length || 0) > 1;
  const singleColorName = product.colors?.length === 1 ? product.colors[0].name : null;
  const colorId = color || product.colors?.[0]?.id;
  const selectedColorName = hasColorSelector
    ? product.colors?.find((c) => c.id === colorId)?.name
    : singleColorName;

  const showWidthSelector = (product.widths?.length || 0) > 1;
  const showLengthSelector = (product.lengths?.length || 0) > 1;
  const singleWidth = product.widths?.length === 1 ? product.widths[0] : null;
  const singleLength = product.lengths?.length === 1 ? product.lengths[0] : null;

  const photos = product.photos || [];
  const photoPool = hasColorSelector ? photos.filter((p) => p.color === colorId) : photos;
  const galleryImages = (photoPool.length ? photoPool : photos).map((p) => p.src);

  // Scheda tecnica: solo campi realmente disponibili.
  const techRows = [
    ["Tipologia", product.tipologia],
    ["Colore", singleColorName || (hasColorSelector ? selectedColorName : null)],
    ["Allungamento (stirata)", product.stretch],
    ["Larghezze", product.widths?.join(", ")],
    ["Lunghezze", product.lengths?.join(", ")],
  ].filter((r) => r[1]);

  const requestFields = [
    ["Categoria", "Bendaggi"],
    ["Famiglia", famLabel],
    ["Marchio", product.brand],
    ["Prodotto", product.fullName || product.name],
    ["Colore", hasColorSelector ? selectedColorName : undefined],
    ["Larghezza", showWidthSelector ? larghezza : singleWidth || undefined],
    ["Lunghezza", showLengthSelector ? lunghezza : singleLength || undefined],
    ["Confezione", product.packages?.length ? confezione : undefined],
  ];

  return (
    <>
      {/* Breadcrumb */}
      <div className="border-b border-[#E7EDF2] bg-white pt-[92px] lg:pt-[104px]">
        <div className="container-x flex flex-wrap items-center gap-2 py-4 text-sm" data-testid="bendaggio-breadcrumb">
          <Link to="/prodotti?cat=bendaggi" className="text-navy/60 transition-colors hover:text-green">Prodotti</Link>
          <span className="text-navy/30">/</span>
          <Link to="/prodotti/bendaggi" data-testid="breadcrumb-bendaggi" className="inline-flex items-center gap-1 text-navy/60 transition-colors hover:text-green">Bendaggi</Link>
          <span className="text-navy/30">/</span>
          <Link to={famLink} data-testid="breadcrumb-famiglia" className="text-navy/60 transition-colors hover:text-green">{famLabel}</Link>
          <span className="text-navy/30">/</span>
          <span className="text-navy/80">{product.name}</span>
        </div>
      </div>

      <section className="py-12 lg:py-16 bg-white" data-testid={`bendaggio-product-${product.slug}`}>
        <div className="container-x grid gap-10 lg:grid-cols-2 lg:gap-16">
          {/* Galleria + colori */}
          <div>
            <Reveal>
              <ProductGallery
                key={colorId}
                images={galleryImages}
                label={hasColorSelector ? `${selectedColorName} · foto in arrivo` : "Foto in arrivo"}
                testid="product-gallery"
              />
            </Reveal>
            {hasColorSelector && (
              <div className="mt-6">
                <ColorSelector colors={product.colors} value={colorId} onChange={setColor} />
                <p className="mt-3 text-xs text-muted-foreground" data-testid="color-disclaimer">
                  I colori visualizzati sono indicativi e possono differire dal prodotto reale.
                </p>
              </div>
            )}
          </div>

          {/* Dati principali */}
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-navy-deep px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white" data-testid="bendaggio-tipologia">{product.tipologia}</span>
              {product.tag && product.tag !== product.tipologia && (
                <span className="inline-flex items-center rounded-full bg-green/12 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-green-deep">{product.tag}</span>
              )}
            </div>
            <div className="mt-3 text-[11px] font-semibold uppercase tracking-wider text-green-deep">{famLabel}</div>
            <h1 className="mt-1.5 font-serif text-4xl lg:text-5xl font-bold text-navy">{product.name}</h1>
            {product.brand && <p className="mt-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">Marchio: {product.brand}</p>}
            <p className="mt-4 text-lg text-navy/80">{product.title}</p>

            {product.features?.length > 0 && (
              <ul className="mt-6 space-y-2.5">
                {product.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-navy/80">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green/12 text-green"><Check size={12} strokeWidth={3} /></span>
                    {f}
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-8 space-y-6">
              {showWidthSelector && <OptionSelector label="Larghezza" testid="larghezza" options={product.widths} value={larghezza} onChange={setLarghezza} />}
              {showLengthSelector && <OptionSelector label="Lunghezza" testid="lunghezza" options={product.lengths} value={lunghezza} onChange={setLunghezza} />}
              {product.packages?.length > 0 && <OptionSelector label="Confezione" testid="confezione" options={product.packages} value={confezione} onChange={setConfezione} />}
            </div>

            <div className="mt-8">
              <button type="button" onClick={() => setModalOpen(true)} data-testid="request-info-open" className="btn-green w-full sm:w-auto">
                Richiedi informazioni <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Descrizione + pannelli tecnici */}
        <div className="container-x mt-14 max-w-3xl">
          <h2 className="font-serif text-2xl font-bold text-navy">Descrizione</h2>
          <p className="mt-3 text-muted-foreground leading-relaxed" data-testid="product-description">{product.description}</p>

          <Accordion type="multiple" className="mt-8" data-testid="product-panels">
            {product.material?.length > 0 && (
              <AccordionItem value="materiale">
                <AccordionTrigger data-testid="panel-materiale" className="text-navy">Composizione / Materiale</AccordionTrigger>
                <AccordionContent><MaterialTable rows={product.material} /></AccordionContent>
              </AccordionItem>
            )}
            {techRows.length > 0 && (
              <AccordionItem value="scheda">
                <AccordionTrigger data-testid="panel-scheda" className="text-navy">Scheda tecnica</AccordionTrigger>
                <AccordionContent><TechTable rows={techRows} /></AccordionContent>
              </AccordionItem>
            )}
            {product.characteristics && (
              <AccordionItem value="caratteristiche">
                <AccordionTrigger data-testid="panel-caratteristiche" className="text-navy">Caratteristiche</AccordionTrigger>
                <AccordionContent><p className="text-sm text-navy/80 leading-relaxed">{product.characteristics}</p></AccordionContent>
              </AccordionItem>
            )}
            {product.indications?.length > 0 && (
              <AccordionItem value="indicazioni">
                <AccordionTrigger data-testid="panel-indicazioni" className="text-navy">Indicazioni</AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-2">
                    {product.indications.map((v) => (<li key={v} className="flex items-start gap-2.5 text-sm text-navy/80"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-green" />{v}</li>))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            )}
            {product.packaging?.length > 0 && (
              <AccordionItem value="confezionamento">
                <AccordionTrigger data-testid="panel-confezionamento" className="text-navy">Confezionamento</AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-2">
                    {product.packaging.map((v) => (<li key={v} className="flex items-start gap-2.5 text-sm text-navy/80"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-green" />{v}</li>))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            )}
            {product.storage && (
              <AccordionItem value="conservazione">
                <AccordionTrigger data-testid="panel-conservazione" className="text-navy">Conservazione</AccordionTrigger>
                <AccordionContent>
                  <p className="flex items-start gap-2 text-sm text-navy/80"><Thermometer size={16} className="mt-0.5 shrink-0 text-green" /> {product.storage}</p>
                </AccordionContent>
              </AccordionItem>
            )}
            {product.properties?.length > 0 && (
              <AccordionItem value="proprieta">
                <AccordionTrigger data-testid="panel-proprieta" className="text-navy">Proprietà</AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-2">
                    {product.properties.map((v) => (<li key={v} className="flex items-start gap-2.5 text-sm text-navy/80"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-green" />{v}</li>))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            )}
            {product.layers?.length > 0 && (
              <AccordionItem value="strati">
                <AccordionTrigger data-testid="panel-strati" className="text-navy">Composizione a tre strati</AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-2">
                    {product.layers.map((r) => (<li key={r[0]} className="flex items-start gap-2.5 text-sm text-navy/80"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-green" /><span><strong className="font-semibold text-navy">{r[0]}:</strong> {r[1]}</span></li>))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            )}
            {product.types?.length > 0 && (
              <AccordionItem value="tipi">
                <AccordionTrigger data-testid="panel-tipi" className="text-navy">Tipi di bendaggio</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    {product.types.map((r) => (
                      <div key={r[0]}>
                        <h4 className="font-serif text-base font-bold text-navy">{r[0]}</h4>
                        <p className="mt-1 text-sm text-navy/80 leading-relaxed">{r[1]}</p>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
            {product.application?.length > 0 && (
              <AccordionItem value="applicazione">
                <AccordionTrigger data-testid="panel-applicazione" className="text-navy">Applicazione corretta</AccordionTrigger>
                <AccordionContent>
                  <p className="mb-3 text-xs italic text-muted-foreground">Indicazioni generali. L&apos;applicazione va effettuata o guidata da personale sanitario.</p>
                  <ul className="space-y-2">
                    {product.application.map((v) => (<li key={v} className="flex items-start gap-2.5 text-sm text-navy/80"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-green" />{v}</li>))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>

          {product.faq?.length > 0 && (
            <div className="mt-12" data-testid="product-faq">
              <h2 className="font-serif text-2xl font-bold text-navy">Domande frequenti</h2>
              <Accordion type="single" collapsible className="mt-6">
                {product.faq.map(([q, a], i) => (
                  <AccordionItem key={q} value={`faq-${i}`}>
                    <AccordionTrigger data-testid={`faq-${i}`} className="text-left text-navy">{q}</AccordionTrigger>
                    <AccordionContent><p className="text-sm text-navy/80 leading-relaxed">{a}</p></AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          {product.warnings && (
            <div className="mt-8 flex items-start gap-3 rounded-2xl border-2 border-amber-400 bg-amber-50 px-5 py-4" data-testid="product-warnings">
              <AlertTriangle size={20} className="mt-0.5 shrink-0 text-amber-600" />
              <div>
                <div className="text-sm font-bold text-amber-800">Avvertenze</div>
                <p className="mt-1 text-sm text-amber-900">{product.warnings}</p>
              </div>
            </div>
          )}

          {product.device && product.healthNote && (
            <p className="mt-8 text-xs text-muted-foreground leading-relaxed" data-testid="product-health-note">{product.healthNote}</p>
          )}

          {/* Torna ai Bendaggi */}
          <div className="mt-10">
            <Link to={famLink} data-testid="back-to-bendaggi" className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy transition-colors hover:text-green">
              <ChevronLeft size={16} /> Torna ai Bendaggi
            </Link>
          </div>
        </div>
      </section>

      {/* Altri prodotti della stessa famiglia */}
      {related.length > 0 && (
        <section className="py-16 lg:py-20 bg-cloud" data-testid="bendaggio-related">
          <div className="container-x">
            <Reveal className="max-w-2xl">
              <span className="eyebrow">Stessa famiglia</span>
              <h2 className="mt-4 font-serif text-2xl lg:text-3xl font-bold text-navy">Altri prodotti della famiglia {famLabel}.</h2>
            </Reveal>
            <Stagger className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((p) => <RelatedCard key={p.slug} p={p} />)}
            </Stagger>
          </div>
        </section>
      )}

      <RequestInfoModal open={modalOpen} onOpenChange={setModalOpen} fields={requestFields} />

      <CTASection />
    </>
  );
};

export default BendaggioProduct;
